"use client";

import { useState } from "react";
import { ChevronDown, Quote } from "lucide-react";
import { testimonials, type Testimonial } from "@/content/testimonials";

function TestimonialCard({ t }: { t: Testimonial }) {
  const paragraphs = t.content.split("\n\n");
  const isExpandable = paragraphs.length > 1;
  const [expanded, setExpanded] = useState(false);

  const visible = isExpandable && !expanded ? paragraphs.slice(0, 1) : paragraphs;

  return (
    <div className="relative bg-canvas-light dark:bg-canvas-dark rounded-[20px] md:rounded-[24px] p-6 md:p-8 lg:p-10 shadow-card dark:shadow-card-dark border border-line-light dark:border-line-dark flex flex-col h-full">
      <Quote className="w-8 h-8 md:w-10 md:h-10 text-line-light dark:text-line-dark mb-4 md:mb-6" />

      <div className="font-body text-[14px] md:text-[15px] lg:text-[16px] leading-[1.7] text-muted-light dark:text-muted-dark italic space-y-3 md:space-y-4 flex-1">
        {visible.map((para, i, arr) => (
          <p key={i}>
            {i === 0 ? "“" : ""}
            {para}
            {!isExpandable || expanded ? (i === arr.length - 1 ? "”" : "") : ""}
          </p>
        ))}
      </div>

      {isExpandable && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="mt-4 md:mt-5 self-start inline-flex items-center gap-1.5 font-body text-[13px] md:text-[14px] font-semibold text-brand-600 dark:text-brand-400 hover:gap-2 transition-all duration-300"
        >
          {expanded ? "Show less" : "Read more"}
          <ChevronDown
            className={`w-4 h-4 transition-transform duration-300 ${expanded ? "rotate-180" : ""}`}
          />
        </button>
      )}

      <div className="flex items-center gap-3 md:gap-4 mt-6 md:mt-8 pt-6 md:pt-8 border-t border-line-light dark:border-line-dark">
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-brand-gradient flex items-center justify-center flex-shrink-0">
          <span className="text-white font-display text-[14px] md:text-[16px] font-bold">
            {t.authorName.charAt(0)}
          </span>
        </div>
        <div>
          <p className="font-body text-[13px] md:text-[14px] font-bold text-ink-light dark:text-ink-dark">
            {t.authorName}
          </p>
          <p className="font-body text-[11px] md:text-[12px] text-faint-light dark:text-faint-dark">
            {t.authorRole} · {t.duration}
          </p>
        </div>
      </div>
    </div>
  );
}

export function TestimonialsSection() {
  return (
    <section id="testimonials" className="py-16 md:py-20 lg:py-28 bg-surface-light dark:bg-surface-dark">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <p className="font-body text-[12px] md:text-[14px] font-semibold uppercase tracking-widest text-faint-light dark:text-faint-dark mb-3 md:mb-4">
            Kind Words
          </p>
          <h2 className="font-display text-[28px] md:text-[32px] lg:text-[40px] font-bold text-ink-light dark:text-ink-dark leading-[1.2]">
            Trusted by
            <span className="text-brand-gradient"> Students &amp; Clients</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-5 md:gap-6 lg:gap-8 items-stretch">
          {testimonials.map((t) => (
            <TestimonialCard key={t.id} t={t} />
          ))}
        </div>
      </div>
    </section>
  );
}

